/**
 * Loads a photo or logo so it can be drawn on the badge canvas without
 * tainting it. Cross-origin images are requested with CORS first; the CDN
 * that serves signed uploads sends no CORS headers, so those fall back to
 * the same-origin `/api/badge-image` proxy.
 */

export const BADGE_IMAGE_PROXY_PATH = "/api/badge-image";

const isCrossOrigin = (src: string): boolean => {
  if (src.startsWith("data:") || src.startsWith("blob:")) return false;
  try {
    return new URL(src, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
};

const loadImage = (
  src: string,
  useCors: boolean
): Promise<HTMLImageElement | null> =>
  new Promise((resolve) => {
    const image = new Image();
    if (useCors) image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });

export const badgeImageProxyUrl = (src: string): string =>
  `${BADGE_IMAGE_PROXY_PATH}?url=${encodeURIComponent(src)}`;

/** Resolves to null when the image cannot be loaded in a canvas-safe way. */
export const loadBadgeImage = async (
  src: string | null | undefined
): Promise<HTMLImageElement | null> => {
  if (!src) return null;
  const crossOrigin = isCrossOrigin(src);
  const direct = await loadImage(src, crossOrigin);
  if (direct || !crossOrigin) return direct;
  return loadImage(badgeImageProxyUrl(src), false);
};
